import { connect } from "react-redux";
import React, { useState } from "react";
import { Button, ModalHeader, ModalBody } from "reactstrap";
import { compose } from "redux";
import NavigationActions from "redux/navigation/actions";
import Loader from "components/Loader";
import { panchangDayImportSchema } from "./validationSchema";
import Papa from "papaparse";
import PanchangPreviewTable from "./PanchangPreviewTable";
import { addBulkPanchangDay } from "services/panchangDayServices";

const { success, error } = NavigationActions;
const ImportEvents = ({ onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      error("Please upload a valid CSV file.");
      return;
    }
    setLoading(true);
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const { data } = results;
          const validationErrors = [];
          const validData = [];

          for (const [index, row] of data.entries()) {
            try {
              // Validate each row using Yup schema

              const gregorianDate =  new Date(row.date).toISOString().split('T')[0];

              const data = {
                vikramSamvatMonth: row?.vikramSamvatMonth?.trim(),
                tithi: row?.tithi?.trim(),
                tithiNo: row?.tithiNo?.trim(),
                paksha: row?.paksha?.trim(),
              }

              // Transform `event.english` and `event.gujarati` into a nested `event` object
              const transformedRow = {
                // ...row,
                ...data,
                gregorianDate,
                vachanamrutTithi: {
                  english: row["vachanamrutTithi.english"] ? row["vachanamrutTithi.english"]?.replace(/_/g, ",") : "",
                  gujarati: row["vachanamrutTithi.gujarati"] ? row["vachanamrutTithi.gujarati"]?.replace(/_/g, ",") : "",
                  hindi: row["vachanamrutTithi.hindi"] ? row["vachanamrutTithi.hindi"]?.replace(/_/g, ",") : "",
                },
                event: {
                  english: row["event.english"] ? row["event.english"]?.replace(/_/g, ",") : "",
                  gujarati: row["event.gujarati"] ? row["event.gujarati"]?.replace(/_/g, ",") : "",
                  hindi: row["event.hindi"] ? row["event.hindi"]?.replace(/_/g, ",") : "",
                },
                swaminarayanSamvat: {
                  english: row["swaminarayanSamvat.english"] ? row["swaminarayanSamvat.english"]?.replace(/_/g, ",") : "",
                  gujarati: row["swaminarayanSamvat.gujarati"] ? row["swaminarayanSamvat.gujarati"]?.replace(/_/g, ",") : "",
                  hindi: row["swaminarayanSamvat.hindi"] ? row["swaminarayanSamvat.hindi"]?.replace(/_/g, ",") : "",
                },
                dateViseVachanamrut: {
                  english: row["dateViseVachanamrut.english"] ? row["dateViseVachanamrut.english"]?.replace(/_/g, ",") : "",
                  gujarati: row["dateViseVachanamrut.gujarati"] ? row["dateViseVachanamrut.gujarati"]?.replace(/_/g, ",") : "",
                  hindi: row["dateViseVachanamrut.hindi"] ? row["dateViseVachanamrut.hindi"]?.replace(/_/g, ",") : "",
                }
              };

              // Remove unused keys from the original row
              delete transformedRow["event.english"];
              delete transformedRow["event.hindi"];
              delete transformedRow["event.gujarati"];
              delete transformedRow["swaminarayanSamvat.english"];
              delete transformedRow["swaminarayanSamvat.gujarati"];
              delete transformedRow["swaminarayanSamvat.hindi"];
              delete transformedRow["dateViseVachanamrut.english"];
              delete transformedRow["dateViseVachanamrut.gujarati"];
              delete transformedRow["dateViseVachanamrut.hindi"];
              delete transformedRow["vachanamrutTithi.english"];
              delete transformedRow["vachanamrutTithi.gujarati"];
              delete transformedRow["vachanamrutTithi.hindi"];

              const validatedRow = await panchangDayImportSchema.validate(transformedRow, {
                abortEarly: false,
              });
              validData.push(validatedRow);
            } catch (validationError) {
              console.error("Error validating CSV:", validationError);

              validationErrors.push({
                row: index + 1,
                errors: validationError.errors,
              });
            }
          }

          setPreviewData(validData);
          setErrors(validationErrors);
        },
      });
    } catch (error) {
      console.error("Error parsing CSV:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (errors.length > 0) {
      error("Please fix errors before submitting.");
      return;
    }
    if (previewData.length === 0) {
      error("No valid data to submit.");
      return;
    }
  
    try {
      // Break the previewData into chunks of 50
      const chunks = [];
      for (let i = 0; i < previewData.length; i += 50) {
        chunks.push(previewData.slice(i, i + 50));
      }
  
      // Create an array of promises for each chunk upload
      const uploadPromises = chunks.map((chunk) =>
        addBulkPanchangDay(token, { data: chunk })
      );
  
      // Execute all chunk uploads in parallel
      const responses = await Promise.all(uploadPromises);
  
      // Check all responses for success or failure
      const allSuccess = responses.every((res) => res?.success);
  
      if (allSuccess) {
        success("All data uploaded successfully.");
        onClose(true);
      } else {
        const failedChunks = responses.filter((res) => !res?.success);
        error(
          `Some chunks failed to upload. Failed chunks: ${failedChunks.length}`
        );
      }
    } catch (err) {
      console.error("Error uploading data:", err);
      alert("An error occurred during the upload process.");
    }
  };
  
  
  return (
    <>
      <ModalHeader toggle={() => onClose()}>
        Import CSV File for Bulk Events
      </ModalHeader>
      <ModalBody>
        <div>
          <input type="file" accept=".csv" onChange={handleFileUpload} />
          <Button
            onClick={handleSubmit}
            className="btn form-button modaladdbutton"
            disabled={loading || errors.length > 0 || previewData.length === 0}
          >
            {loading ? "Uploading..." : "Upload"}
          </Button>

          <div>
            {previewData?.length > 0 && (
              <>
                <h4>Preview</h4>
                <PanchangPreviewTable data={previewData} />
              </>
            )}
            {errors?.length > 0 && (
              <div>
                <h4>Errors</h4>
                {errors?.map((err) => (
                  <p key={err?.row}>
                    Row {err?.row}: {err?.errors?.join(", ") || "Invalid Data"}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      {loading ? <Loader /> : ""}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.themeChanger,
    token: state.auth.accessToken,
  };
};

export default compose(connect(mapStateToProps, { success, error }))(
  ImportEvents
);
